import costService from '@Apis/cost.api';
import rehabService from '@Apis/rehab.api';
import {
  CostTableRequestData,
  GetCostTableGridPaginatedRequest,
  CostTableDetailData,
} from '@Types/costTable.types';
import { AxiosResponse } from 'axios';

interface CreateCostTableResponse {
  body: {
    cost_table_id: string;
  };
  statusCode: number;
}

interface CopyCostTableRequestData {
  cost_table_id: string;
}

interface CopyCostTableResponse {
  body: {
    new_cost_table_id: string;
    message: string;
  };
  statusCode: number;
}

interface DeleteCostTableRequestData {
  cost_table_id: string;
}

interface DeleteCostTableResponse {
  body: {
    message: string;
  };
  statusCode: number;
}

export interface GetCostTablesOverviewResponse {
  pagination: {
    page: number;
    slice: number;
    total: number;
    pages: number;
  };
  totalCount: number;
  data: {
    id: string;
    name: string;
    asset_group_set: string;
    cost_per: string;
    last_updated: string;
  }[];
}

interface GetCostTablesApiResponse {
  body: {
    data: {
      id: string;
      name: string;
      asset_group_set_id: string;
      cost_per: string;
      updated_at: string;
    }[];
    pagination: {
      page: number;
      slice: number;
      total: number;
      pages: number;
    };
  };
  statusCode: number;
}

export const getCostTableOverviewData = async (
  request: GetCostTableGridPaginatedRequest
): Promise<AxiosResponse<GetCostTablesOverviewResponse>> => {
  try {
    const response = await costService.get<GetCostTablesApiResponse>(
      'cost-tables',
      {
        params: request,
      }
    );

    const transformedData: GetCostTablesOverviewResponse = {
      pagination: response.data.body.pagination,
      totalCount: response.data.body.pagination.total,
      data: response.data.body.data.map(item => ({
        id: item.id,
        name: item.name,
        asset_group_set: item.asset_group_set_id,
        cost_per: item.cost_per,
        last_updated: item.updated_at,
      })),
    };

    return {
      ...response,
      data: transformedData,
    };
  } catch (error: any) {
    console.error('[getCostTableOverviewData] Error occurred:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });
    throw error;
  }
};

export const newCostTable = async (
  request: CostTableRequestData
): Promise<AxiosResponse<CreateCostTableResponse>> => {
  try {
    const payload = {
      name: request.name,
      asset_group_set_id: request.assetGroupSetId,
      cost_per: request.costPer,
    };
    return await costService.post<CreateCostTableResponse>(
      'cost-tables',
      payload
    );
  } catch (error: any) {
    console.error('[newCostTable] Error occurred:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });
    throw error;
  }
};

export const copyCostTable = async (
  request: CopyCostTableRequestData
): Promise<AxiosResponse<CopyCostTableResponse>> => {
  try {
    return await costService.post<CopyCostTableResponse>(
      `cost-tables/${request.cost_table_id}/copy`
    );
  } catch (error: any) {
    console.error('[copyCostTable] Error occurred:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });
    throw error;
  }
};

export const deleteCostTable = async (
  request: DeleteCostTableRequestData
): Promise<AxiosResponse<DeleteCostTableResponse>> => {
  try {
    return await costService.post<DeleteCostTableResponse>(
      `cost-tables/${request.cost_table_id}/delete`
    );
  } catch (error: any) {
    console.error('[deleteCostTable] Error occurred:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });
    throw error;
  }
};

export const updateCostTableName = (request: {
  costTableId: string;
  newCostTableName: string;
}) => {
  return costService.post<{ cost_table_id: string }>(
    `cost-tables/${request.costTableId}/update_name`,
    {
      new_cost_table_name: request.newCostTableName,
    }
  );
};

export const updateCostTableConfiguration = (request: {
  costTableId: string;
  newUnit: string;
}) => {
  return costService.post<{ cost_table_id: string }>(
    `cost-tables/${request.costTableId}/update_unit`,
    {
      cost_per: request.newUnit,
    }
  );
};

export const updateCostTableData = (request: {
  costTableId: string;
  latestCostTableDataId: string;
  updatedValues: Record<string, CostTableDetailData>;
}) => {
  const payload = Object.entries(request.updatedValues).map(([id, data]) => ({
    asset_group_id: id,
    asset_group_name: data.assetGroupSet,
    asset_group_description: data.description,
    asset_count: data.assetCount,
    unit_cost: data.unitCost,
  }));

  return costService.post<{ cost_table_id: string }>(
    `cost-tables/${request.costTableId}/update_costs`,
    {
      latest_cost_table_data_id: request.latestCostTableDataId,
      data: payload,
    }
  );
};

interface GetCostTableConfigResponse {
  body: {
    cost_table: {
      id: string;
      name: string;
      asset_group_set_id: string;
      cost_per: string;
      updated_at: string;
      latest_cost_table_data_id: string;
    };
  };
  statusCode: number;
}

interface GetCostTableDataResponse {
  body: {
    data: {
      asset_group_id: string;
      asset_group_name: string;
      asset_group_description: string;
      asset_count: number;
      unit_cost: number;
    }[];
  };
  statusCode: number;
}

export interface GetCostTableDetailsResponse {
  cost_table: {
    id: string;
    name: string;
    asset_group_set: string;
    cost_per: string;
    last_updated: string;
    latest_cost_table_data_id: string;
  };
  cost_table_data: {
    id: string;
    asset_group_set: string;
    description: string;
    asset_count: number;
    unit_cost: number;
  }[];
}

export const getCostTableDetails = async (
  costTableId: string
): Promise<AxiosResponse<GetCostTableDetailsResponse>> => {
  try {
    const configResponse = await costService.get<GetCostTableConfigResponse>(
      `cost-tables/${costTableId}`
    );

    const costTableConfig = configResponse.data.body.cost_table;
    const dataId = costTableConfig.latest_cost_table_data_id;

    const dataResponse = await costService.get<GetCostTableDataResponse>(
      `cost-tables/${costTableId}/data/${dataId}`
    );

    const transformedResponse: GetCostTableDetailsResponse = {
      cost_table: {
        id: costTableConfig.id,
        name: costTableConfig.name,
        asset_group_set: costTableConfig.asset_group_set_id,
        cost_per: costTableConfig.cost_per,
        last_updated: costTableConfig.updated_at,
        latest_cost_table_data_id: costTableConfig.latest_cost_table_data_id,
      },
      cost_table_data: dataResponse.data.body.data.map(item => ({
        id: item.asset_group_id,
        asset_group_set: item.asset_group_name,
        description: item.asset_group_description,
        asset_count: item.asset_count,
        unit_cost: item.unit_cost,
      })),
    };

    return {
      ...configResponse,
      data: transformedResponse,
    };
  } catch (error: any) {
    console.error('[getCostTableDetails] Error occurred:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });
    throw error;
  }
};

export const refreshCostTableData = (id: string) => {
  return costService.post(`cost-tables/${id}/refresh`);
};

export const checkCostTableInUse = async (
  costTableId: string
): Promise<boolean> => {
  try {
    const response = await rehabService.get<{
      data: { inUse: boolean; count: number };
    }>(`rehabTree/byCostTable/${costTableId}`);
    return response.data.data.inUse;
  } catch {
    // Fail open: if the check cannot be performed, allow deletion to proceed.
    return false;
  }
};
